'use client';

import React, { useState, forwardRef, useEffect } from 'react';
import classnames from 'classnames';

import Box from '../Box/Box';
import Iconography from '../Iconography/Iconography';
import Typography from '../Typography/Typography';

import styles from './Switch.module.scss';

type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';
type LabelPosition = 'left' | 'top';

// For react-hook-form compatibility
interface RegisterFunction {
  (name: string): {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
}

export interface SwitchProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  theme?: Theme;
  /**
   * for use with react-hook-form
   */
  name?: string;
  /**
   * for use with react-hook-form
   */
  register?: RegisterFunction;
  labelLeft?: string;
  labelRight?: string;
  labelPosition?: LabelPosition;
  icon?: boolean;
  checked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onClick?: (checked: boolean) => void;
  direction?: Direction;
  dataTestId?: string;
  /**
   * for use with datalayer
   */
  linkContext?: string;
  /**
   * for use with datalayer
   */
  gaComponent?: string;
  logEvents?: boolean;
}

const Switch = forwardRef<HTMLDivElement, SwitchProps>(
  (
    {
      theme = 'dark',
      name = '', //for use with react-hook-form
      register = undefined, //for use with react-hook-form
      labelLeft = '',
      labelRight = '',
      labelPosition = 'left',
      icon = false,
      checked = false,
      disabled = false,
      readOnly = false,
      onClick = () => {},
      direction = 'ltr',
      dataTestId = 'switchButton',
      gaComponent = 'Switch', // for use with datalayer
      linkContext = '', // for use with datalayer
      logEvents = true,
      ...props
    },
    ref
  ) => {
    const [isChecked, setIsChecked] = useState(checked);

    useEffect(() => {
      setIsChecked(checked);
    }, [checked]);

    const renderLabel = (labelText: string) => (
      <Typography
        theme={theme}
        color={disabled || readOnly ? 'gray-3' : 'white'}
        variant="body"
        size="small"
        direction={direction}
      >
        {labelText}
      </Typography>
    );

    const handleClick = (e: React.MouseEvent<HTMLLabelElement>) => {
      e.preventDefault();

      if (readOnly || disabled) return;
      if (typeof window !== 'undefined' && logEvents) {
        const datalayerObject = {
          event: 'interaction',
          interaction__type: 'selection',
          interaction__text: `${labelLeft || labelRight || 'Switch button'}: ${!isChecked ? 'on' : 'off'}`,
          interaction__linkContext: linkContext,
          interaction__component: gaComponent,
        };
        (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(datalayerObject);
      }
      setIsChecked(!isChecked);
      onClick && onClick(!isChecked);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLLabelElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const mockMouseEvent = {
          preventDefault: () => {},
        } as React.MouseEvent<HTMLLabelElement>;
        handleClick(mockMouseEvent);
      }
    };

    return (
      <Box
        className={classnames(styles.switchWrapper, {
          [styles.labelTop]: labelPosition === 'top',
        })}
        testId={dataTestId}
        ref={ref}
        {...props}
      >
        {labelLeft && renderLabel(labelLeft)}
        <input
          id="switchButton"
          checked={isChecked}
          {...(register ? register(name) : { name })}
          readOnly={true}
          className={styles.hiddenCheckbox}
          type="checkbox"
        />
        <label
          htmlFor="switchButton"
          className={classnames(styles.switchLabel, styles[theme], {
            [styles.checked]: isChecked,
            [styles.disabled]: disabled,
            [styles.readOnly]: readOnly,
          })}
          onClick={handleClick}
          onKeyDown={handleKeyPress}
          tabIndex={0}
          role="switch"
          aria-checked={isChecked}
        >
          <span
            className={classnames(
              styles.switchButton,
              styles[`${theme}`],
              styles[`direction-${direction}`],
              {
                [styles.checked]: isChecked,
                [styles.disabled]: disabled,
                [styles.readOnly]: readOnly,
              }
            )}
          >
            {icon && (
              <Iconography category="global" name={isChecked ? 'checkmark' : 'close'} size={16} />
            )}
          </span>
        </label>
        {labelRight && labelPosition !== 'top' && renderLabel(labelRight)}
      </Box>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
